import React, { useContext, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AuthContext from '../contexts/AuthContext';
import NotificationContext from '../contexts/NotificationContext';
import logo from '../logo.jpg';
import './Sidebar.css';

const ADMIN = ['OWNER', 'SUPER_ADMIN', 'INTERMEDIATE_ADMIN'];

/**
 * 역할마다 볼 수 있는 곳.
 *
 * 서버가 막긴 하지만, 눌러보고 막히는 것과 처음부터 없는 것은 다르다. 경비원에게
 * 사용자 관리와 점검 계획이 보이면 자기 일이 어디 있는지 찾기부터 해야 한다.
 *
 * 근무자에게는 '오늘 할 점검' 하나면 된다. 시설을 열고 구역을 골라 들어가던
 * 길은 그 화면이 대신한다.
 *
 * 매일 여는 것은 접지 않는다. 오늘 할 점검과 주간 점검 현황, 이슈는 하루에도
 * 몇 번씩 여는 곳이라, 묶어 두면 그 횟수만큼 서랍을 여는 품이 든다. 한 번
 * 정해 놓고 드물게 고치는 것 -- 점검 계획과 점검표 -- 만 묶는다.
 *
 * 묶음에 하나만 남는 역할은 묶지 않고 그대로 낸다. 하나짜리 서랍은 여는 품만
 * 든다.
 */
const MENU = [
  { to: '/', icon: 'bi-speedometer2', label: '대시보드', roles: [...ADMIN, 'CLIENT'] },
  { to: '/today', icon: 'bi-check2-square', label: '오늘 할 점검', roles: [...ADMIN, 'STAFF'] },
  { to: '/inspection-matrix', icon: 'bi-grid-3x3', label: '주간 점검 현황', roles: [...ADMIN, 'CLIENT'] },
  { to: '/reports', icon: 'bi-exclamation-triangle-fill', label: '이슈', roles: [...ADMIN, 'STAFF', 'CLIENT'] },
  { to: '/statistics', icon: 'bi-graph-up-arrow', label: '통계', roles: [...ADMIN, 'CLIENT'] },
  {
    key: 'facility',
    icon: 'bi-geo-alt-fill',
    label: '시설',
    children: [
      { to: '/facilities', icon: 'bi-geo-alt-fill', label: '시설 목록', roles: ADMIN },
      // 임대차는 건물을 두고 하는 일이라 여기 든다. 계약이 바뀌는 것은 한 해에
      // 몇 번뿐이라, 매일 여는 것과 같은 줄에 둘 이유가 없다.
      // 건물주도 본다. 임대차계약을 건물주 이름으로 맺으므로 그분 계약이다.
      // 고치는 것은 서버가 막는다.
      { to: '/leases', icon: 'bi-file-earmark-text', label: '임대차', roles: [...ADMIN, 'CLIENT'] },
      // 건물을 맡긴 사람. 건물에 매이는 값이라 시설 아래에 둔다.
      { to: '/clients', icon: 'bi-person-badge', label: '건물주', roles: ADMIN },
    ],
  },
  {
    key: 'inspection-setup',
    icon: 'bi-sliders',
    // '점검' 이라고만 하면 위의 '오늘 할 점검' 과 갈리지 않는다. 저기는 하러
    // 가는 곳이고 여기는 정해 두는 곳이다.
    label: '점검 설정',
    children: [
      { to: '/inspection-plans', icon: 'bi-calendar-check', label: '점검 계획', roles: ADMIN },
      { to: '/checklist-management', icon: 'bi-clipboard-check-fill', label: '점검표', roles: ADMIN },
    ],
  },
  // '작업자' 는 관리자가 근무자만 떠올리게 한다. 이 화면은 근무자부터 소유자까지
  // 계정과 권한을 다루는 곳이다.
  { to: '/workers', icon: 'bi-people-fill', label: '사용자', roles: ADMIN },
  { to: '/settings', icon: 'bi-gear-fill', label: '설정', roles: [...ADMIN, 'STAFF', 'CLIENT'] },
];

/** 이 역할이 볼 수 있는 것만 남긴다. 묶음은 자식이 없으면 통째로 빠진다. */
const visibleFor = (role) =>
  MENU.map((m) => {
    if (!m.children) return m.roles.includes(role) ? m : null;
    const children = m.children.filter((c) => c.roles.includes(role));
    if (children.length === 0) return null;
    // 하나만 남으면 묶지 않는다.
    if (children.length === 1) return children[0];
    return { ...m, children };
  }).filter(Boolean);

const Sidebar = ({ isOpen, toggleSidebar }) => {
  const { user, logout } = useContext(AuthContext);
  const { unreadCount, openPanel } = useContext(NotificationContext);
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const items = useMemo(() => (user ? visibleFor(user.role) : []), [user]);

  // 접었다 편 것만 기억한다. 손대지 않은 묶음은 지금 보고 있는 화면이 그 안에
  // 있으면 펴 둔다 -- 어디에 있는지 스스로 말하지 않는 서랍은 길잡이가 아니다.
  const [toggled, setToggled] = useState({});
  const isGroupOpen = (group) =>
    toggled[group.key] ?? group.children.some((c) => isActive(c.to));

  if (!user) return null;

  return (
    <aside className={`sidebar ${isOpen ? 'open' : ''}`}>
      <div className="sidebar-header">
        <Link
          to={user.role === 'STAFF' ? '/today' : '/'}
          className="sidebar-brand d-flex align-items-center gap-2"
        >
          <img src={logo} alt="로고" height="36" className="rounded" />
          {user.company && (
            <div className="d-flex flex-column text-start ms-2">
              <span className="small text-white-50" style={{ fontSize: '0.8rem' }}>{user.company.name}</span>
            </div>
          )}
        </Link>
        <div className="d-flex align-items-center gap-2">
          <button className="btn btn-link text-white p-0 d-none d-md-block position-relative me-2" onClick={openPanel}>
            <i className="bi bi-bell fs-5"></i>
            {unreadCount > 0 && (
              <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger shadow" style={{ fontSize: '0.65rem' }}>
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
          <button className="btn btn-link d-md-none text-white p-0" onClick={toggleSidebar}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
      </div>

      <nav className="sidebar-nav">
        <div className="nav-section">
          {items.map((m) =>
            m.children ? (
              <div key={m.key} className="nav-group">
                <button
                  type="button"
                  className={`nav-item nav-group-head ${isGroupOpen(m) ? '' : 'collapsed'}`}
                  onClick={() =>
                    setToggled((prev) => ({ ...prev, [m.key]: !isGroupOpen(m) }))
                  }
                  aria-expanded={isGroupOpen(m)}
                >
                  <i className={`bi ${m.icon}`}></i>
                  <span className="flex-grow-1 text-start">{m.label}</span>
                  <i className={`bi bi-chevron-${isGroupOpen(m) ? 'up' : 'down'} nav-caret`}></i>
                </button>
                {isGroupOpen(m) && (
                  <div className="nav-children">
                    {m.children.map((c) => (
                      <Link
                        key={c.to}
                        to={c.to}
                        className={`nav-item nav-child ${isActive(c.to) ? 'active' : ''}`}
                        onClick={toggleSidebar}
                      >
                        <i className={`bi ${c.icon}`}></i>
                        <span>{c.label}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={m.to}
                to={m.to}
                className={`nav-item ${isActive(m.to) ? 'active' : ''}`}
                onClick={toggleSidebar}
              >
                <i className={`bi ${m.icon}`}></i>
                <span>{m.label}</span>
              </Link>
            ),
          )}
        </div>
      </nav>

      <div className="sidebar-footer">
        <Link to="/help" onClick={toggleSidebar} className="d-flex align-items-center mb-3 text-white-50 text-decoration-none" style={{ cursor: 'pointer' }}>
          <i className="bi bi-question-circle me-2"></i>
          <span className="small">도움말</span>
        </Link>
        <div className="user-info">
          <div className="avatar">
            {user.email.charAt(0).toUpperCase()}
          </div>
          <div className="user-details flex-grow-1">
            <span className="user-email">{user.email}</span>
            <div className="d-flex align-items-center gap-1">
              <span className="user-role">{user.role === 'OWNER' ? '소유자' : user.role === 'SUPER_ADMIN' ? '관리자' : '직원'}</span>
              {user.company && <span className="badge bg-dark-subtle text-white-50 border-0 p-0" style={{ fontSize: '0.65rem' }}>• {user.company.name}</span>}
            </div>
          </div>
          <button onClick={handleLogout} className="btn btn-link text-white-50 p-0 ms-2" title="로그아웃">
            <i className="bi bi-box-arrow-right fs-5"></i>
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
